import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, Linkedin, Github, Mail, ArrowLeft, Sparkles, Code2, Brain, Users, Youtube } from "lucide-react";

const FOUNDER = {
  name: "Bharani Sivakumar",
  role: "Founder & Editor · TrillionBytex",
  bio: "Builder of TrillionBytex — a modern learning platform designed to give every student access to live classes, brain-training games, real-world coding challenges, and an AI mentor that never sleeps. Passionate about turning curiosity into skill, and skill into shipped software.",
  linkedin: "https://in.linkedin.com/in/bharani-s-44a41b2a6",
  github: "https://github.com/bharanisivakumar6374-cmd",
  youtube: "https://www.youtube.com/watch?v=x09MECz3F5A",
  email: "bharani.sivakumar6374@gmail.com",
};

const PILLARS = [
  { icon: Users, title: "Role-based access", body: "Purpose-built dashboards for students, staff, and admins — everyone sees only what they need." },
  { icon: Sparkles, title: "AI mentor: 02", body: "A Gemini-powered assistant that recommends courses, breaks down concepts, and helps debug your code." },
  { icon: Brain, title: "Brain Games", body: "Memory, math, pattern recall, sudoku, typing sprint, and slider puzzles to sharpen cognitive skills." },
  { icon: Code2, title: "Coding Practice", body: "Beginner-friendly challenges in JS, HTML/CSS, Python, C, and C++ — with an in-browser editor and instant output." },
];

export default function AboutPage() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#0B1120] text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="about-home-link" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center"><GraduationCap className="w-5 h-5" /></div>
            <div>
              <div className="font-display font-black text-lg leading-none">TrillionBytex<span className="text-blue-400">edu</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Smart TBX Learning Hub</div>
            </div>
          </Link>
          <Link to="/login" data-testid="about-signin-link" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Sign in</Link>
        </div>
      </div>

      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-3">About Smart TBX Learning Hub</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-slate-900 leading-tight">
              AI-powered education,<br /> coding practice, brain games<br /> <span className="text-blue-600">and career-focused learning.</span>
            </h1>
            <p className="mt-5 text-slate-600 text-base max-w-2xl leading-relaxed">
              Smart TBX Learning Hub is TrillionBytex&apos;s flagship learning platform. We blend structured live cohorts, in-browser coding challenges (JS · HTML · CSS · Python · C · C++), brain-training games, quizzes, and an always-on AI mentor called <span className="font-semibold text-slate-900">02</span>. Everything a curious learner needs, built for real career growth.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register" data-testid="about-cta-register" className="px-5 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors text-sm">Start learning</Link>
              <a href={FOUNDER.linkedin} target="_blank" rel="noreferrer" data-testid="about-cta-linkedin" className="px-5 py-3 rounded-full border border-slate-300 text-slate-800 hover:border-blue-500 transition-colors text-sm inline-flex items-center gap-2"><Linkedin className="w-4 h-4" /> Connect with the founder</a>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="bg-[#0B1120] text-white rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-blue-500/20 blur-3xl" />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-violet-500/20 blur-3xl" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.3em] text-blue-400">Founder</div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 text-white flex items-center justify-center font-display font-black text-xl">BS</div>
                  <div>
                    <div className="font-display font-black text-xl">{FOUNDER.name}</div>
                    <div className="text-xs text-slate-400">{FOUNDER.role}</div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-slate-300 leading-relaxed">{FOUNDER.bio}</p>
                <div className="mt-5 space-y-2">
                  <a href={FOUNDER.linkedin} target="_blank" rel="noreferrer" data-testid="founder-linkedin" className="flex items-center gap-2 text-sm text-slate-300 hover:text-blue-400"><Linkedin className="w-4 h-4" /> LinkedIn Profile</a>
                  <a href={FOUNDER.github} target="_blank" rel="noreferrer" data-testid="founder-github" className="flex items-center gap-2 text-sm text-slate-300 hover:text-violet-400"><Github className="w-4 h-4" /> GitHub</a>
                  <a href={FOUNDER.youtube} target="_blank" rel="noreferrer" data-testid="founder-youtube" className="flex items-center gap-2 text-sm text-slate-300 hover:text-red-400"><Youtube className="w-4 h-4" /> YouTube</a>
                  <a href={`mailto:${FOUNDER.email}`} data-testid="founder-email" className="flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-400"><Mail className="w-4 h-4" /> {FOUNDER.email}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-2">What we build</div>
        <h2 className="font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight max-w-xl">Four pillars, one learning experience.</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-white border border-slate-200 rounded-2xl p-5 card-hover" data-testid={`pillar-${title.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
              <h3 className="mt-3 font-display font-bold text-lg text-slate-900">{title}</h3>
              <p className="mt-1 text-sm text-slate-500 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0B1120] text-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-400 mb-3">Our mission</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight leading-tight">
            Make world-class education <span className="text-blue-400">personal</span>, <span className="text-emerald-400">practical</span>, and <span className="text-violet-400">playful</span> — for every learner.
          </h2>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <button onClick={() => nav(-1)} data-testid="about-previous-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black text-white text-sm hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4" /> Previous Page
        </button>
      </div>

      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
          <div>© {new Date().getFullYear()} TrillionBytexedu · Built by Bharani Sivakumar</div>
          <div className="flex items-center gap-4">
            <a href={FOUNDER.linkedin} target="_blank" rel="noreferrer" className="hover:text-blue-400 inline-flex items-center gap-1"><Linkedin className="w-3.5 h-3.5" /> LinkedIn</a>
            <a href={FOUNDER.github} target="_blank" rel="noreferrer" className="hover:text-violet-400 inline-flex items-center gap-1"><Github className="w-3.5 h-3.5" /> GitHub</a>
            <a href={FOUNDER.youtube} target="_blank" rel="noreferrer" className="hover:text-red-500 inline-flex items-center gap-1"><Youtube className="w-3.5 h-3.5" /> YouTube</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
